// src/pages/RegistrarBitacoras.jsx
import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import Swal from "sweetalert2";

/* =================== Helpers de UI =================== */
const EmptyState = ({
  title = "Sin registros",
  subtitle = "Aún no has agregado actividades.",
}) => (
  <div className="text-center text-muted py-4">
    <div className="display-6 mb-2">🗒️</div>
    <div className="fw-semibold">{title}</div>
    <div className="small">{subtitle}</div>
  </div>
);

const Pill = ({ children, variant = "secondary" }) => (
  <span className={`badge rounded-pill text-bg-${variant}`}>{children}</span>
);

// Utils
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
const safe = (v, fallback = "—") => (v ?? v === 0 ? v : fallback);

/* =================== Modal: Tabla de Detalles =================== */
const DetalleTableModal = ({
  show,
  onClose,
  actividades,
  onDelete,
  bitacoraIndex,
}) => {
  if (!show) return null;
  return (
    <>
      <div
        className="modal fade show"
        style={{ display: "block" }}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content shadow-lg border-0">
            <div className="modal-header border-0 pb-0">
              <div>
                <h5 className="modal-title mb-1">
                  Detalles —{" "}
                  <span className="text-success">Bitácora {bitacoraIndex + 1}</span>
                </h5>
                <div className="small text-muted">
                  <i className="fa-regular fa-rectangle-list me-1"></i>
                  {actividades.length} actividad
                  {actividades.length !== 1 ? "es" : ""} registrada
                  {actividades.length !== 1 ? "s" : ""}
                </div>
              </div>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>

            <div className="modal-body pt-3">
              {actividades.length === 0 ? (
                <EmptyState
                  title="Sin actividades"
                  subtitle="Usa 'Añadir actividad' para crearlas."
                />
              ) : (
                <div className="table-responsive rounded-3 border">
                  <table className="table table-sm table-striped align-middle mb-0">
                    <thead className="table-light">
                      <tr className="text-muted">
                        <th style={{ minWidth: 240 }}>Descripción de la Actividad</th>
                        <th style={{ width: 120 }}>Inicio</th>
                        <th style={{ width: 120 }}>Fin</th>
                        <th style={{ minWidth: 200 }}>Evidencia</th>
                        <th style={{ minWidth: 220 }}>Observaciones</th>
                        <th className="text-end" style={{ width: 80 }}>
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {actividades.map((a, i) => (
                        <tr key={`act-${i}`}>
                          <td className="fw-semibold">
                            {a.descripcion || (
                              <em className="text-muted">Sin descripción</em>
                            )}
                          </td>
                          <td>
                            <Pill variant="light">{a.inicio || "—"}</Pill>
                          </td>
                          <td>
                            <Pill variant="light">{a.fin || "—"}</Pill>
                          </td>
                          <td className="text-truncate" style={{ maxWidth: 280 }}>
                            {a.evidencia || <span className="text-muted">—</span>}
                          </td>
                          <td className="text-truncate" style={{ maxWidth: 280 }}>
                            {a.observaciones || <span className="text-muted">—</span>}
                          </td>
                          <td className="text-end">
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => onDelete(i)}
                              title="Eliminar"
                            >
                              <i className="fa-solid fa-trash"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="table-light">
                      <tr>
                        <td colSpan={6} className="small text-muted py-2 px-3">
                          <i className="fa-regular fa-circle-question me-1"></i>
                          Consejo: mantén tus descripciones claras y concretas; agrega
                          la evidencia necesaria (link/archivo/entregable).
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>

            <div className="modal-footer border-0">
              <button className="btn btn-secondary" onClick={onClose}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Backdrop */}
      <div className="modal-backdrop fade show"></div>
    </>
  );
};

/* =================== Modal: Formulario de Actividad =================== */
const ActividadFormModal = ({ show, onClose, onSave }) => {
  const initial = {
    descripcion: "",
    inicio: "",
    fin: "",
    evidencia: "",
    observaciones: "",
  };
  const [form, setForm] = useState(initial);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (show) {
      setForm(initial);
      setTouched(false);
    }
  }, [show]);

  if (!show) return null;

  const isValid = () => !!form.descripcion && !!form.inicio && !!form.fin;

  const guardar = () => {
    setTouched(true);
    if (!isValid()) return;
    // SOLO estado local: onSave no persiste en BD
    onSave(form);
    setForm(initial);
    setTouched(false);
  };

  const invalid = (field) => touched && !form[field];

  return (
    <>
      <div
        className="modal fade show"
        style={{ display: "block" }}
        role="dialog"
        aria-modal="true"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content shadow-lg border-0">
            <div className="modal-header border-0 pb-0">
              <div>
                <h5 className="modal-title mb-1">Añadir actividad</h5>
                <div className="small text-muted">
                  <i className="fa-regular fa-pen-to-square me-1"></i>
                  Completa los campos requeridos <Pill variant="danger">(*)</Pill>
                </div>
              </div>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>

            <div className="modal-body pt-3">
              <div className="mb-3">
                <label className="form-label small fw-semibold">
                  Descripción de la Actividad <span className="text-danger">*</span>
                </label>
                <textarea
                  className={`form-control ${invalid("descripcion") ? "is-invalid" : ""}`}
                  rows={3}
                  value={form.descripcion}
                  onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                  placeholder="Describe la actividad realizada…"
                />
                {invalid("descripcion") && (
                  <div className="invalid-feedback">La descripción es obligatoria.</div>
                )}
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">
                    Fecha de Inicio <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className={`form-control ${invalid("inicio") ? "is-invalid" : ""}`}
                    value={form.inicio}
                    onChange={(e) => setForm({ ...form, inicio: e.target.value })}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">
                    Fecha de Fin <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className={`form-control ${invalid("fin") ? "is-invalid" : ""}`}
                    value={form.fin}
                    onChange={(e) => setForm({ ...form, fin: e.target.value })}
                  />
                </div>
              </div>

              <div className="mt-3">
                <label className="form-label small fw-semibold">Evidencia de Cumplimiento</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={form.evidencia}
                  onChange={(e) => setForm({ ...form, evidencia: e.target.value })}
                  placeholder="Documento, producto, proceso, entregable u otro…"
                />
                <div className="form-text">
                  Puedes adjuntar recursos en el sistema de archivos y referenciarlos aquí.
                </div>
              </div>

              <div className="mt-3">
                <label className="form-label small fw-semibold">Observaciones / Comentarios</label>
                <textarea
                  className="form-control"
                  rows={2}
                  value={form.observaciones}
                  onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                  placeholder="Dificultades, inasistencias o comentarios…"
                />
              </div>
            </div>

            <div className="modal-footer border-0">
              <button className="btn btn-outline-secondary" onClick={onClose}>
                Cerrar
              </button>
              <button className="btn btn-success" onClick={guardar}>
                <i className="fa-solid fa-check me-1"></i> Guardar actividad
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Backdrop */}
      <div className="modal-backdrop fade show"></div>
    </>
  );
};

/* =================== Página: RegistrarBitacoras (Professional UI) =================== */
const RegistrarBitacoras = () => {
  const { etapaId, matriculaId, aprendizId } = useParams();
  const formRef = useRef(null);

  // location + flag de modo masivo
  const location = useLocation();
  const isMassive = location.pathname.startsWith(
    "/etapaproductiva/bitacoras/registromasivo/"
  );

  // Estado principal
  const [bitacoras, setBitacoras] = useState(Array.from({ length: 12 }, () => []));
  const [bitacoraIds, setBitacoraIds] = useState(Array.from({ length: 12 }, () => null));
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({
    etapa: null,
    aprendiz: null,
    matricula: null,
  });

  // filtros/toolbar
  const [query, setQuery] = useState("");
  const [onlyWithData, setOnlyWithData] = useState(false);

  // modales
  const [showTable, setShowTable] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [activeIdx, setActiveIdx] = useState(null);

  // ====== Estado y lógica de REGISTRO MASIVO ======
  const [bulkForm, setBulkForm] = useState({
    etapaPracticaObjectId: "",
    numeroDesde: 1,
    numeroHasta: 12,
    estadoBitacora: "BORRADOR",
    soloActivos: true,
    skipWhenNoEtapa: true,
    mergeMode: "merge", // "merge" | "replace" | "skip"
    activitiesDefault: [], // [{ descripcionActividad, fechaInicioActividad, ... }]
    activitiesByNumero: {}, // { "1":[...], "2":[...] }
  });
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);

  const bulkSet = (k, v) => setBulkForm((prev) => ({ ...prev, [k]: v }));

  // helpers para actividades por defecto (si tu UI tiene inputs ya dibujados, bindea estos)
  const bulkAddActividad = () => {
    bulkSet("activitiesDefault", [
      ...bulkForm.activitiesDefault,
      {
        descripcionActividad: "",
        fechaInicioActividad: "",
        fechaFinActividad: "",
        evidenciaActividad: "",
        observacionesActividad: "",
      },
    ]);
  };
  const bulkChangeActividad = (idx, value) => {
    const next = [...bulkForm.activitiesDefault];
    next[idx] = value;
    bulkSet("activitiesDefault", next);
  };
  const bulkRemoveActividad = (idx) => {
    const next = [...bulkForm.activitiesDefault];
    next.splice(idx, 1);
    bulkSet("activitiesDefault", next);
  };

  const bulkValidate = () => {
    if (!matriculaId) {
      Swal.fire("Atención", "No se recibió el parámetro matriculaId.", "warning");
      return false;
    }
    const d = Number(bulkForm.numeroDesde) || 1;
    const h = Number(bulkForm.numeroHasta) || 12;
    if (d < 1 || d > 12 || h < 1 || h > 12) {
      Swal.fire("Atención", "El rango debe estar entre 1 y 12.", "info");
      return false;
    }
    return true;
  };

  // ====== NUEVO: construir activitiesByNumero desde el estado local ======
  const mapActividadToPayload = (a) => ({
    descripcionActividad: a.descripcion || "",
    fechaInicioActividad: a.inicio || "",
    fechaFinActividad: a.fin || "",
    evidenciaActividad: a.evidencia || "",
    observacionesActividad: a.observaciones || "",
  });

  const buildActivitiesByNumeroFromState = () => {
    const byNumero = {};
    bitacoras.forEach((rows, idx) => {
      if (Array.isArray(rows) && rows.length > 0) {
        byNumero[String(idx + 1)] = rows.map(mapActividadToPayload);
      }
    });
    return byNumero;
  };

  // === Deduplicar actividades dentro de cada número de bitácora (cliente) ===
  const dedupeActs = (arr) => {
    const seen = new Set();
    return (arr || []).filter((a) => {
      const key = [
        (a.descripcionActividad || "").trim().toLowerCase(),
        a.fechaInicioActividad || "",
        a.fechaFinActividad || "",
        (a.evidenciaActividad || "").trim().toLowerCase(),
        (a.observacionesActividad || "").trim().toLowerCase(),
      ].join("|");
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  // ====== Bulk call (usa lo local por defecto) ======
  const callBulk = async (dryRun, useLocal = true) => {
    if (!bulkValidate()) return;
    setBulkLoading(true);
    setBulkPreview(null);
    try {
      // 1) Construir desde estado local cuando useLocal=true
      const activitiesByNumeroRaw = useLocal
        ? buildActivitiesByNumeroFromState()
        : bulkForm.activitiesByNumero;

      // 2) Dedupe por número de bitácora (cliente)
      const activitiesByNumero = {};
      Object.keys(activitiesByNumeroRaw || {}).forEach((k) => {
        activitiesByNumero[k] = dedupeActs(activitiesByNumeroRaw[k]);
      });

      // 3) En modo "usar locales", NO mandamos activitiesDefault para evitar doble inyección
      const activitiesDefaultToSend = useLocal ? [] : bulkForm.activitiesDefault || [];

      // 4) En ruta de registro masivo, forzar replace para evitar merge duplicado con lo existente
      const mergeModeToSend = isMassive ? "replace" : bulkForm.mergeMode || "merge";

      const body = {
        etapaPracticaObjectId: bulkForm.etapaPracticaObjectId || undefined, // opcional
        numeroDesde: Number(bulkForm.numeroDesde),
        numeroHasta: Number(bulkForm.numeroHasta),
        estadoBitacora: bulkForm.estadoBitacora,
        soloActivos: !!bulkForm.soloActivos,
        skipWhenNoEtapa: !!bulkForm.skipWhenNoEtapa,
        dryRun: !!dryRun,
        mergeMode: mergeModeToSend,
        activitiesDefault: activitiesDefaultToSend,
        activitiesByNumero,
      };

      const { data } = await axios.post(`/api/bitacoras/bulk/${matriculaId}`, body);
      setBulkPreview(data);

      if (dryRun) {
        Swal.fire({
          icon: "info",
          title: "Previsualización generada",
          html: `
            <div class="text-start">
              <div><b>Aprendices afectados:</b> ${data?.totalAprendices ?? 0}</div>
              <div><b>Bitácoras objetivo:</b> ${data?.totalBitacorasObjetivo ?? 0}</div>
            </div>
          `,
        });
      } else {
        if (
          (data?.summary?.createdCount ?? 0) === 0 &&
          Array.isArray(data?.summary?.skippedWithoutEtapa) &&
          data.summary.skippedWithoutEtapa.length > 0
        ) {
          Swal.fire({
            icon: "warning",
            title: "No se crearon bitácoras",
            html:
              "Parece que los aprendices no tienen etapa asignada y <b>skipWhenNoEtapa</b> está activo.<br/>" +
              "Desactívalo o envía <b>etapaPracticaObjectId</b> para forzar la creación.",
          });
        } else {
          Swal.fire({
            icon: "success",
            title: "Proceso completado",
            text: data?.message || "Bitácoras procesadas correctamente.",
          });
        }
        // refrescar (si aplica)
        getData();
      }
    } catch (error) {
      const msg = error?.response?.data?.message || error.message || "Error en la operación.";
      Swal.fire("Error", msg, "error");
    } finally {
      setBulkLoading(false);
    }
  };

  // Helpers de fecha a Y-M-D (UI -> payload single)
  const toYMD = (d) => {
    if (!d) return "";
    try {
      const dt = new Date(d);
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, "0");
      const da = String(dt.getDate()).padStart(2, "0");
      return `${y}-${m}-${da}`;
    } catch {
      return "";
    }
  };
  const nombreAprendiz = () => {
    const a = meta?.aprendiz;
    if (!a) return "—";
    return (
      fullName(
        a?.nombre1Aprendiz,
        a?.nombre2Aprendiz,
        a?.apellido1Aprendiz,
        a?.apellido2Aprendiz
      ) || "—"
    );
  };

  // ===== Helpers para nombres del Instructor y del Jefe =====
  const fullName = (p1, p2, a1, a2) =>
    [p1, p2, a1, a2].filter(Boolean).join(" ").trim() || "—";

  // Atajos a estructura profunda
  const grupo = meta?.matricula?.grupoObjectId;
  const programa = grupo?.programaObjectId;

  // Ente (prioriza la etapa, luego matrícula)
  const nombreEnte =
    meta?.etapa?.nombreEnteconformador ??
    meta?.matricula?.enteObjectId?.nombreEnteconformador ??
    "—";

  // Instructor y Jefe desde meta.etapa (poblados en backend)
  const instructor = meta?.etapa?.instructorObjectId || null;
  const jefe = meta?.etapa?.jefeObjectId || null;

  const instructorNombre = instructor
    ? fullName(
        instructor?.nombre1Instructor,
        instructor?.nombre2Instructor,
        instructor?.apellido1Instructor,
        instructor?.apellido2Instructor
      )
    : "—";

  const jefeNombre = jefe
    ? fullName(
        jefe?.nombre1Jefe,
        jefe?.nombre2Jefe,
        jefe?.apellido1Jefe,
        jefe?.apellido2Jefe
      )
    : "—";

  // Fechas de etapa (admite ambos nombres de campos)
  const etapaFechaInicio =
    meta?.etapa?.fechaInicio || meta?.etapa?.fechaInicioEtapaPractica;
  const etapaFechaFin =
    meta?.etapa?.fechaFin || meta?.etapa?.fechaFinEtapaPractica;

  // GET que carga bitácoras + meta según IDs
  const getData = useCallback(async () => {
    // En modo masivo, probablemente no tenemos etapaId ni aprendizId => no bloquear UI
    if (!etapaId || !aprendizId || !matriculaId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(
        `/api/bitacoras/listbyrelacion/${etapaId}/${aprendizId}/${matriculaId}`
      );

      // 12 slots
      const next = Array.from({ length: 12 }, () => []);
      const nextIds = Array.from({ length: 12 }, () => null);

      // Mapea cada bitácora por numeroBitacora (1..12) al índice 0..11
      (data?.bitacoras || []).forEach((b) => {
        const idx = Number(b?.numeroBitacora) - 1;
        if (idx >= 0 && idx < 12) {
          const acts = Array.isArray(b?.actividadesBitacora)
            ? b.actividadesBitacora.map((a) => ({
                descripcion: a?.descripcionActividad || "",
                inicio: toYMD(a?.fechaInicioActividad),
                fin: toYMD(a?.fechaFinActividad),
                evidencia: a?.evidenciaActividad || "",
                observaciones: a?.observacionesActividad || "",
              }))
            : [];
          next[idx] = acts;
          nextIds[idx] = b?._id || null;
        }
      });

      setBitacoras(next);
      setBitacoraIds(nextIds);

      // Meta desde backend
      setMeta({
        etapa: data?.meta?.etapa || null,
        aprendiz: data?.meta?.aprendiz || null,
        matricula: data?.meta?.matricula || null,
      });
    } catch (err) {
      console.error("Error cargando bitácoras:", err?.message);
      setBitacoras(Array.from({ length: 12 }, () => []));
      setBitacoraIds(Array.from({ length: 12 }, () => null));
      setMeta({ etapa: null, aprendiz: null, matricula: null });
    } finally {
      setLoading(false);
    }
  }, [etapaId, aprendizId, matriculaId]);

  useEffect(() => {
    getData();
  }, [getData]);

  // Guardar actividad desde el form modal (UI local)
  const addActividad = (actividad) => {
    if (activeIdx === null) return;
    setBitacoras((prev) => {
      const copy = [...prev];
      copy[activeIdx] = [...copy[activeIdx], actividad];
      return copy;
    });
  };

  // Eliminar actividad desde la tabla (UI local)
  const deleteActividad = (idxFila) => {
    if (activeIdx === null) return;
    setBitacoras((prev) => {
      const copy = [...prev];
      const rows = [...copy[activeIdx]];
      rows.splice(idxFila, 1);
      copy[activeIdx] = rows;
      return copy;
    });
  };

  // Limpiar bitácora (UI local)
  const clearBitacora = (idxB) => {
    setBitacoras((prev) => {
      const copy = [...prev];
      copy[idxB] = [];
      return copy;
    });
  };

  /* ========== Crear / Actualizar (single) ========== */
  const saveBitacora = async (numeroBitacora) => {
    // En modo masivo, anular por completo el guardado individual
    if (isMassive) return;

    try {
      if (!etapaId || !aprendizId || !matriculaId) {
        Swal.fire({
          icon: "warning",
          title: "Faltan identificadores",
          text: "Verifica etapaId, aprendizId y matriculaId.",
        });
        return;
      }
      const rows = bitacoras[numeroBitacora - 1] || [];
      if (rows.length === 0) {
        Swal.fire({
          icon: "info",
          title: `Bitácora ${numeroBitacora}`,
          text: "No tiene actividades para guardar.",
        });
        return;
      }
      const newBitacora = {
        etapaPracticaObjectId: etapaId,
        aprendizObjectId: aprendizId,
        matriculaObjectId: matriculaId,
        numeroBitacora: Number(numeroBitacora),
        actividadesBitacora: rows.map(mapActividadToPayload),
      };
      await axios.post("/api/bitacoras/add", newBitacora);

      await getData();
      Swal.fire({
        icon: "success",
        title: `Bitácora ${numeroBitacora} creada con éxito`,
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      alert(error.response?.data?.message || "Error al guardar");
    }
  };

  const updateBitacora = async (numeroBitacora) => {
    // En modo masivo, anular por completo el guardado individual
    if (isMassive) return;

    try {
      const bitId = bitacoraIds[numeroBitacora - 1];
      if (!bitId) {
        Swal.fire({
          icon: "warning",
          title: "No existe en BD",
          text: `La Bitácora ${numeroBitacora} no tiene _id para actualizar.`,
        });
        return;
      }
      const rows = bitacoras[numeroBitacora - 1] || [];
      const newBitacora = {
        actividadesBitacora: rows.map(mapActividadToPayload),
      };
      await axios.put(`/api/bitacoras/update/${bitId}`, newBitacora);

      await getData();
      Swal.fire({
        icon: "success",
        title: `Bitácora ${numeroBitacora} actualizada con éxito`,
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      alert(error.response?.data?.message || "Error al actualizar");
    }
  };

  // onSubmit del form: en modo masivo no persiste, sólo tip
  const actions = async (e) => {
    e.preventDefault();
    if (isMassive) {
      Swal.fire({
        icon: "info",
        title: "Modo registro masivo",
        text: "Las actividades se guardaron localmente. Usa 'Guardar Masivo' para persistir en BD.",
        timer: 1400,
        showConfirmButton: false,
      });
      return;
    }
    const numero = activeIdx + 1;
    if (bitacoraIds[numero - 1]) {
      await updateBitacora(numero);
    } else {
      await saveBitacora(numero);
    }
  };

  // Filtro de tarjetas por título/índice y si tienen datos
  const filtered = bitacoras
    .map((rows, i) => ({ rows, index: i }))
    .filter(({ rows, index }) => {
      const matches = `bitacora ${index + 1}`
        .toLowerCase()
        .includes(query.toLowerCase());
      const passData = onlyWithData ? rows.length > 0 : true;
      return matches && passData;
    });

  // ===== Excel
  const [downloading, setDownloading] = useState(false);
  const descargarExcelBitacora = async (numeroBitacora) => {
    try {
      if (!Number.isInteger(numeroBitacora) || numeroBitacora < 1 || numeroBitacora > 12) {
        Swal.fire("Atención", "Número de bitácora inválido.", "info");
        return;
      }
      const bitId = bitacoraIds[numeroBitacora - 1];
      if (!bitId) {
        Swal.fire({
          icon: "info",
          title: `Bitácora ${numeroBitacora} aún no existe`,
          text: "Guárdala primero para poder generar el Excel.",
        });
        return;
      }

      if (downloading) return;
      setDownloading(true);

      const resp = await axios.get(`/api/bitacora/generar-excel/${bitId}`, {
        responseType: "blob",
      });

      const cd = resp.headers?.["content-disposition"] || "";
      const match = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
      const docAprendiz = meta?.aprendiz?.documentoAprendiz || "SIN_DOC";
      const fallbackName = `Bitacora_${numeroBitacora}_${docAprendiz}.xlsx`;
      const fileName = match ? decodeURIComponent(match[1]) : fallbackName;

      const blob = new Blob([resp.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: "success",
        title: `Excel de Bitácora ${numeroBitacora} generado`,
        showConfirmButton: false,
        timer: 1400,
      });
    } catch (error) {
      console.error("Error al generar Excel:", error);
      const msg =
        error.response?.data?.message ||
        error.response?.statusText ||
        "No se pudo generar el Excel de la bitácora.";
      Swal.fire("Error", msg, "error");
    } finally {
      setDownloading(false);
    }
  };

  const tituloPagina = isMassive
    ? "Registro Masivo de Bitácoras"
    : "Bitácoras de Etapa Productiva";

  return (
    <form ref={formRef} onSubmit={actions} className="container-fluid py-4 bg-light">
      {/* Encabezado contextual con meta */}
      {!loading && !isMassive && (
        <div className="row g-3 mb-4">
          {/* Aprendiz + Matrícula */}
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-solid fa-user-graduate"></i>
                  </div>
                  <div className="w-100">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="fw-semibold">{nombreAprendiz()}</div>
                    </div>
                    <div className="small text-muted mt-1">
                      Documento: {safe(meta?.aprendiz?.documentoAprendiz)} · Tel:{" "}
                      {safe(meta?.aprendiz?.telefono1Aprendiz)} · Email:{" "}
                      {safe(meta?.aprendiz?.emailAprendiz)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Etapa productiva */}
          <div className="col-12 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-success-subtle text-success d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-regular fa-calendar-check"></i>
                  </div>
                  <div>
                    <div className="small text-muted">Fecha de la Etapa Productiva</div>
                    <div className="fw-semibold">
                      {fmtDate(etapaFechaInicio)} — {fmtDate(etapaFechaFin)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Programa */}
          <div className="col-12 col-md-6 col-lg-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-info-subtle text-info d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-solid fa-book"></i>
                  </div>
                  <div>
                    <div className="small text-muted">Programa de Formación</div>
                    <div className="fw-semibold">{safe(programa?.nombrePrograma)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Grupo */}
          <div className="col-12 col-md-6 col-lg-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-warning-subtle text-warning d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-solid fa-people-group"></i>
                  </div>
                  <div>
                    <div className="small text-muted">Grupo</div>
                    <div className="fw-semibold">
                      {safe(grupo?.codigoGrupo, meta?.matricula?.fichaGrupo ?? "—")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Ente Conformador */}
          <div className="col-12 col-md-6 col-lg-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-secondary-subtle text-secondary d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-solid fa-building"></i>
                  </div>
                  <div>
                    <div className="small text-muted">Ente Conformador</div>
                    <div className="fw-semibold">{safe(nombreEnte)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Instructor de seguimiento */}
          <div className="col-12 col-md-6 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-primary-subtle text-primary d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-solid fa-chalkboard-user"></i>
                  </div>
                  <div>
                    <div className="small text-muted">Instructor de Seguimiento</div>
                    <div className="fw-semibold">{safe(instructorNombre)}</div>
                    <div className="small text-muted mt-1">
                      Doc: {safe(instructor?.documentoInstructor)} · Tel:{" "}
                      {safe(instructor?.telefono1Instructor ?? instructor?.telefonoInstructor)} ·
                      Email: {safe(instructor?.emailInstructor)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Jefe inmediato (ente) */}
          <div className="col-12 col-md-6 col-lg-6">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <div className="d-flex align-items-start gap-3">
                  <div
                    className="rounded-circle bg-dark-subtle text-dark d-flex align-items-center justify-content-center"
                    style={{ width: 42, height: 42 }}
                  >
                    <i className="fa-solid fa-user-tie"></i>
                  </div>
                  <div>
                    <div className="small text-muted">Jefe Inmediato</div>
                    <div className="fw-semibold">{safe(jefeNombre)}</div>
                    <div className="small text-muted mt-1">
                      Doc: {safe(jefe?.documentoJefe)} · Tel:{" "}
                      {safe(jefe?.telefono1Jefe ?? meta?.etapa?.enteObjectId?.jefeTelefono)} ·
                      Email: {safe(jefe?.emailJefe ?? meta?.etapa?.enteObjectId?.jefeCorreo)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 mb-3">
        <div>
          <h1 className="fw-bold mb-1">{tituloPagina}</h1>
        </div>

        {/* Toolbar */}
        <div className="d-flex flex-wrap align-items-center gap-2">
          <div className="input-group">
            <span className="input-group-text bg-white">
              <i className="fa-solid fa-magnifying-glass"></i>
            </span>
            <input
              type="search"
              className="form-control"
              placeholder="Buscar por número de bitácora…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="form-check ms-1">
            <input
              id="chk-data"
              className="form-check-input"
              type="checkbox"
              checked={onlyWithData}
              onChange={(e) => setOnlyWithData(e.target.checked)}
            />
            <label htmlFor="chk-data" className="form-check-label small">
              Sólo con actividades
            </label>
          </div>
        </div>
      </div>

      {/* ====== Acciones MASIVAS ====== */}
      <div className="d-flex flex-wrap align-items-center gap-2 mb-4">
        {isMassive && (
          <>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={bulkLoading}
              onClick={() => callBulk(false, true)}
              title="Usa las actividades locales por número como plantilla; guarda en BD para TODOS los aprendices de la matrícula"
            >
              <i className="fa-solid fa-cloud-arrow-up me-1" />
              Guardar Masivo
            </button>
          </>
        )}
      </div>

      {/* Grid 12 tarjetas */}
      <div className="row g-4">
        {filtered.map(({ rows, index: idxB }) => (
          <div className="col-12 col-md-6 col-lg-4" key={`bitacora-${idxB}`}>
            <div className="card h-100 shadow-sm border-0">
              <div className="card-header bg-white border-0 d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="rounded-circle bg-success-subtle text-success d-flex align-items-center justify-content-center"
                    style={{ width: 36, height: 36 }}
                  >
                    <i className="fa-regular fa-note-sticky"></i>
                  </div>
                  <div className="d-flex align-items-center">
                    <div className="fw-semibold">Bitácora {idxB + 1}</div>
                    {bitacoraIds[idxB] && !isMassive && (
                      <span className="badge text-bg-light ms-2">EXISTENTE</span>
                    )}
                  </div>
                </div>

                {/* Acciones (dropdown) */}
                <div className="dropdown">
                  <button
                    className="btn btn-sm btn-outline-secondary dropdown-toggle"
                    data-bs-toggle="dropdown"
                  >
                    Acciones
                  </button>
                  <ul className="dropdown-menu dropdown-menu-end">
                    <li>
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => {
                          setActiveIdx(idxB);
                          setShowTable(true);
                        }}
                      >
                        <i className="fa-solid fa-table me-2"></i> Ver detalles (tabla)
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => {
                          setActiveIdx(idxB);
                          setShowForm(true);
                        }}
                      >
                        <i className="fa-solid fa-plus me-2"></i> Añadir actividad
                      </button>
                    </li>
                    <li>
                      <hr className="dropdown-divider" />
                    </li>

                    <li>
                      <button
                        className="dropdown-item text-danger"
                        onClick={() => clearBitacora(idxB)}
                      >
                        <i className="fa-solid fa-eraser me-2"></i> Limpiar bitácora
                      </button>
                    </li>

                    {/* Exportar a Excel solo tiene sentido si existe en BD */}
                    {!isMassive && (
                      <li>
                        <button
                          type="button"
                          className="dropdown-item"
                          onClick={() => {
                            setActiveIdx(idxB);
                            descargarExcelBitacora(idxB + 1);
                          }}
                        >
                          <i className="fa-solid fa-file-excel me-2"></i>
                          Exportar a Excel
                        </button>
                      </li>
                    )}
                  </ul>
                </div>
              </div>

              <div className="card-body">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="small text-muted">Los detalles no se muestran aquí.</div>
                  <Pill variant={rows.length ? "success" : "secondary"}>
                    {rows.length} registro{rows.length !== 1 ? "s" : ""}
                  </Pill>
                </div>
                {/* progress visual simple */}
                <div className="progress mt-3" style={{ height: 6 }}>
                  <div
                    className={`progress-bar ${rows.length ? "bg-success" : "bg-secondary"}`}
                    style={{ width: `${Math.min(rows.length * 10, 100)}%` }}
                    role="progressbar"
                    aria-valuenow={rows.length}
                    aria-valuemin="0"
                    aria-valuemax="10"
                  />
                </div>
              </div>

              <div className="card-footer bg-white d-flex justify-content-between align-items-center">
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    setActiveIdx(idxB);
                    setShowTable(true);
                  }}
                >
                  <i className="fa-solid fa-table me-1"></i> Detalles
                </button>

                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={() => {
                    setActiveIdx(idxB);
                    setShowForm(true);
                  }}
                >
                  <i className="fa-solid fa-plus me-1"></i> Añadir
                </button>
              </div>
            </div>
          </div>
        ))}

        {filtered.length === 0 && (
          <div className="col-12">
            <EmptyState
              title="Sin coincidencias"
              subtitle="Intenta con otro término o desmarca 'Sólo con actividades'."
            />
          </div>
        )}
      </div>

      {/* Modal de Tabla (detalles) */}
      {showTable && activeIdx !== null && (
        <DetalleTableModal
          show={showTable}
          onClose={() => {
            setShowTable(false);
            setActiveIdx(null);
          }}
          actividades={bitacoras[activeIdx]}
          onDelete={(i) => {
            setBitacoras((prev) => {
              const copy = [...prev];
              const rows = [...copy[activeIdx]];
              rows.splice(i, 1);
              copy[activeIdx] = rows;
              return copy;
            });
          }}
          bitacoraIndex={activeIdx}
        />
      )}

      {/* Modal de Formulario (agregar) */}
      {showForm && activeIdx !== null && (
        <ActividadFormModal
          show={showForm}
          onClose={() => {
            setShowForm(false);
            setActiveIdx(null);
          }}
          onSave={addActividad}
        />
      )}
    </form>
  );
};

export default RegistrarBitacoras;
